// ============================================================
// UI-Primitives — Card, Badge, Info, StatusBadge, NetCell
// ============================================================

import type { CSSProperties, ReactNode } from 'react';
import { CO } from '../theme';
import { eur, sEur } from '../format';
import type { SpielStatus } from '../types';

export function Card({ children, style, padding }: { children: ReactNode; style?: CSSProperties; padding?: number | string }) {
  const pad = padding === undefined ? 20 : padding;
  return (
    <div
      style={{
        background: CO.card,
        border: `1px solid ${CO.border}`,
        borderRadius: 12,
        padding: typeof pad === 'number' ? `${pad}px` : pad,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function CardTitle({ title, sub, right }: { title: ReactNode; sub?: ReactNode; right?: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
      <div>
        <div style={{ fontSize: 15, fontWeight: 600, color: CO.text }}>{title}</div>
        {sub && <div style={{ fontSize: 12.5, color: CO.muted, marginTop: 2 }}>{sub}</div>}
      </div>
      {right}
    </div>
  );
}

export function Info({ text }: { text: string }) {
  return (
    <span
      title={text}
      tabIndex={0}
      role="note"
      aria-label={text}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 15,
        height: 15,
        borderRadius: '50%',
        border: `1px solid ${CO.faint}`,
        color: CO.muted,
        fontSize: 10,
        fontWeight: 700,
        marginLeft: 6,
        cursor: 'help',
        verticalAlign: 'middle',
        lineHeight: 1,
      }}
    >
      i
    </span>
  );
}

export function Badge({ text, color, bg }: { text: ReactNode; color: string; bg: string }) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 9px',
        borderRadius: 20,
        fontSize: 11.5,
        fontWeight: 600,
        color,
        background: bg,
        whiteSpace: 'nowrap',
      }}
    >
      {text}
    </span>
  );
}

const STATUS_MAP: Record<SpielStatus, [string, string]> = {
  teilnehmend: ['#7a5b12', '#f7edcf'],
  'in Auswertung': ['#5b4870', '#ece4f0'],
  abgeschlossen: ['#425c68', '#e6edf0'],
  archiviert: ['#5a5f56', '#eceee9'],
};

export function StatusBadge({ s }: { s: SpielStatus }) {
  const c = STATUS_MAP[s] ?? ['#5a5f56', '#eceee9'];
  return <Badge text={s} color={c[0]} bg={c[1]} />;
}

export function NetCell({ n, tone }: { n: number; tone?: 'pos' | 'neg' }) {
  const color =
    tone === 'pos'
      ? CO.pos
      : tone === 'neg'
        ? CO.neg
        : n > 0
          ? CO.pos
          : n < 0
            ? CO.neg
            : CO.muted;
  return (
    <span className="num" style={{ color, fontWeight: 600 }}>
      {sEur(n)}
    </span>
  );
}

export function EurCell({ value, tone }: { value: number; tone?: 'einsatz' | 'auszahlung' | 'pos' | 'neg' }) {
  let color: string = CO.text;
  if (tone === 'einsatz') color = CO.einsatz;
  else if (tone === 'auszahlung') color = CO.auszahlung;
  else if (tone === 'pos') color = CO.pos;
  else if (tone === 'neg') color = CO.neg;
  return (
    <span className="num" style={{ color, fontWeight: 600 }}>
      {eur(value)}
    </span>
  );
}
